import React, { useState, useEffect, useRef } from "react";
import "./LandingPage.css";

const LandingPage = () => {
  const [displayText, setDisplayText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const typingRef = useRef(null);
  const containerRef = useRef(null);
  
  const fullText = "Kredete Labs is one unified operating system that replaces every tool a business uses to operate. From banking and payments to customer support, product development, and AI automation; all in one platform, powered by AI agents that work autonomously across every function.";
  
  // Mouse tracking for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setMousePosition({ x, y });
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  // Typewriter effect
  useEffect(() => {
    let currentIndex = 0;
    
    const startTyping = () => {
      typingRef.current = setInterval(() => {
        if (currentIndex <= fullText.length) {
          setDisplayText(fullText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(typingRef.current);
          setIsTyping(false);
          setTimeout(() => {
            currentIndex = 0;
            setDisplayText("");
            setIsTyping(true);
          }, 4000);
        }
      }, 35);
    };
    
    if (isTyping) {
      startTyping();
    }
    
    return () => {
      if (typingRef.current) {
        clearInterval(typingRef.current);
      }
    };
  }, [isTyping]);

  return (
    <div className="landing-container" ref={containerRef}>
      {/* Futuristic Grid Background */}
      <div className="grid-bg"></div>
      
      {/* Glowing Orbs */}
      <div 
        className="orb orb-1"
        style={{
          transform: `translate(${mousePosition.x * -40}px, ${mousePosition.y * -40}px)`
        }}
      ></div>
      <div 
        className="orb orb-2"
        style={{
          transform: `translate(${mousePosition.x * 30}px, ${mousePosition.y * 30}px)`
        }}
      ></div>
      <div 
        className="orb orb-3"
        style={{
          transform: `translate(${mousePosition.x * -20}px, ${mousePosition.y * 20}px)`
        }}
      ></div>
      
      {/* Floating Particles */}
      <div className="particles">
        {[...Array(40)].map((_, i) => (
          <div key={i} className={`particle particle-${(i % 20) + 1}`} />
        ))}
      </div>
      
      {/* Light Beams */}
      <div className="light-beams">
        <div className="beam beam-1"></div>
        <div className="beam beam-2"></div>
        <div className="beam beam-3"></div>
      </div>
      
      {/* Header */}
      <header className="header">
        <div className="logo-pill">
          <span className="logo-icon">◆</span>
          <span className="logo-text">kredete labs</span>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="main-content">
        {/* Text Above Image */}
        <div className="text-container">
          <p className="mission-text">
            {displayText}
            <span className={`cursor ${isTyping ? 'blinking' : ''}`}>|</span>
          </p>
        </div>
        
        {/* LABS Image */}
        <div 
          className="labs-image"
          style={{
            transform: `translate(${mousePosition.x * 15}px, ${mousePosition.y * 15}px)`
          }}
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_ai-business-os-4/artifacts/5qpo7dbr_Untitled%20design%20%2823%29.png" 
            alt="Kredete Labs" 
          />
          <div className="image-reflection"></div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <span className="footer-label">Company</span>
          <a href="#news" className="footer-link">Company News</a>
          <div className="social-links">
            <a href="https://x.com/KredeteLabs" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="X (Twitter)">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/kredetelabs" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
